import React, { useState, useEffect, useRef } from 'react';
import { createTask, getTaskById, updateTask, addAssigneeToTask, deleteTask, uploadTaskAttachment, deleteTaskAttachment } from '../../../utils/api';
import TaskRecurringSection from './TaskRecurringSection';
import TaskCommentsActivitySection from './TaskCommentsActivitySection';
import ProseDescriptionEditor from '../../common/ProseDescriptionEditor';
import StatusDropdown, { getStatusLabel, getStatusColor } from '../../common/StatusDropdown';
import './TaskDetailSidePanel.scss';

// Subtask Row Component for TaskDetailSidePanel
const SubtaskRow = ({ subtask, taskListId, parentTaskId, projectUsers, onUpdate, onDelete, onOpenSubtaskDetail, projectId, currentUserId, parentTaskData, isTaskOwner }) => {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editTitle, setEditTitle] = useState(subtask.title || subtask.name || '');
    const [showActionMenu, setShowActionMenu] = useState(false);
    const titleInputRef = useRef(null);
    const actionMenuRef = useRef(null);

    const isSubtaskOwner = subtask.created_by === parseInt(currentUserId);

    const isParentTaskOwner = parentTaskData && parentTaskData.created_by === parseInt(currentUserId);

    // Sync state with subtask prop changes
    useEffect(() => {
        setEditTitle(subtask.title || subtask.name || '');
    }, [subtask]);

    // Close action menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowActionMenu(false);
            }
        };

        if (showActionMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showActionMenu]);

    const handleSaveTitle = () => {
        if (editTitle.trim() && editTitle.trim() !== (subtask.title || subtask.name)) {
            if (onUpdate) {
                onUpdate(subtask.id, parentTaskId, taskListId, { title: editTitle.trim(), action: 'update_title' });
            }
        }
        setIsEditingTitle(false);
    };

    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveTitle();
        } else if (e.key === 'Escape') {
            setEditTitle(subtask.title || subtask.name || '');
            setIsEditingTitle(false);
        }
    };

    const handleStatusChange = (newStatus) => {
        if (onUpdate) {
            onUpdate(subtask.id, parentTaskId, taskListId, { status: newStatus, action: 'update_status' });
        }
    };

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this subtask?')) {
            if (onDelete) {
                onDelete(subtask.id, parentTaskId, taskListId);
            }
        }
        setShowActionMenu(false);
    };

    return (
        <tr className="task-detail-subtask-row">
            <td className="task-detail-subtask-col-name">
                <div className="task-detail-subtask-name-cell">
                    <StatusDropdown
                        currentStatus={subtask.status ?? 0}
                        onStatusChange={handleStatusChange}
                        className="task-detail-subtask-status-wrapper"
                        size="medium"
                    />
                    {isEditingTitle ? (
                        <input
                            ref={titleInputRef}
                            type="text"
                            className="task-detail-subtask-title-input"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onBlur={handleSaveTitle}
                            onKeyDown={handleTitleKeyDown}
                            autoFocus
                        />
                    ) : (
                        <div className="task-detail-subtask-title-wrapper" style={{ opacity: subtask.status === 1 ? 0.5 : 1 }}>
                            <span 
                                className={"task-detail-subtask-title " + (subtask.status === 1 ? 'asana-completed-task' : '')}
                                onClick={() => {
                                    setIsEditingTitle(true);
                                    setEditTitle(subtask.title || subtask.name || '');
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                {subtask.title || subtask.name}
                            </span>
                            <button
                                type="button"
                                className="asana-task-action-btn"
                                onClick={() => {
                                    onOpenSubtaskDetail(projectId, subtask.id);
                                }}
                            >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            </td>
            <td className="task-detail-subtask-col-actions">
                <div className="task-detail-subtask-actions-wrapper" ref={actionMenuRef}>
                    <button
                        type="button"
                        className="task-detail-subtask-action-btn"
                        onClick={() => setShowActionMenu(!showActionMenu)}
                    >
                        <i className="fa-solid fa-ellipsis-v"></i>
                    </button>
                    {showActionMenu && (
                        <div className="task-detail-subtask-action-menu">
                            <button
                                type="button"
                                className="task-detail-subtask-action-menu-item"
                                onClick={() => {
                                    setIsEditingTitle(true);
                                    setEditTitle(subtask.title || subtask.name || '');
                                    setShowActionMenu(false);
                                }}
                            >
                                <i className="fa-solid fa-eye"></i>
                                View
                            </button>

                            {(isSubtaskOwner || isParentTaskOwner) && (
                                <button
                                    type="button"
                                    className="task-detail-subtask-action-menu-item task-detail-subtask-action-delete"
                                    onClick={handleDelete}
                                >
                                    <i className="fa-solid fa-trash"></i>
                                    Delete
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
};

const TaskDetailSidePanel = ({ 
    taskId, 
    projectId,
    currentUserId,
    onClose,
    onOpenSubtaskDetail,
    onRefreshMainTaskData,
    OnNotFound,
}) => {
    const [taskTitle, setTaskTitle] = useState('');
    const [taskDescription, setTaskDescription] = useState('');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
    const [showDueDatePicker, setShowDueDatePicker] = useState(false);
    const [showStartDatePicker, setShowStartDatePicker] = useState(false);
    const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
    const [assigneeSearchResults, setAssigneeSearchResults] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState(0); // 0=low, 1=medium, 2=high
    const [subtaskText, setSubtaskText] = useState('');
    const [isAddingSubtask, setIsAddingSubtask] = useState(false);
    
    const assigneeSearchRef = useRef(null);
    const assigneeDropdownRef = useRef(null);
    const datePickerWrapperRef = useRef(null);
    const startDatePickerWrapperRef = useRef(null);
    const titleInputRef = useRef(null);
    const subtaskInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const descriptionEditorRef = useRef(null);
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);

    // Helper function to map assignees
    const mapAssignee = (assigneeData) => {
        if (!assigneeData) return null;
        return {
            ID: assigneeData.user_id || assigneeData.ID || assigneeData.id,
            id: assigneeData.user_id || assigneeData.ID || assigneeData.id,
            display_name: assigneeData.display_name || assigneeData.name,
            name: assigneeData.display_name || assigneeData.name,
            image: assigneeData.assignee || assigneeData.image || assigneeData.avatar_url,
            avatar_url: assigneeData.assignee || assigneeData.image || assigneeData.avatar_url,
            user_email: assigneeData.user_email || assigneeData.email,
            created_by: assigneeData.created_by || assigneeData.createdBy,
            updated_by: assigneeData.updated_by || assigneeData.updatedBy
        };
    };

    const mapAssignees = (assigneesData) => {
        if (!assigneesData || !Array.isArray(assigneesData)) return [];
        return assigneesData.map(mapAssignee).filter(Boolean);
    };

    const [assignees, setAssignees] = useState(() => {
        const assigneesData = [];
        return mapAssignees(assigneesData);
    });
    const [isLoadingTask, setIsLoadingTask] = useState(true);
    const [taskData, setTaskData] = useState(null);
    const [projectName, setProjectName] = useState('');
    const [refreshTaskActivities, setRefreshTaskActivities] = useState(false);

    // Fetch task details by ID when component mounts or task ID changes
    useEffect(() => {
        const fetchTaskDetails = async () => {
            if (!taskId || !projectId) return;
            
            try {
                const response = await getTaskById(projectId, taskId);

                console.log('response', response);
                if (response && response.data) {
                    const fetchedTask = response.data;
                    setTaskData(fetchedTask);
                }else{
                    if(typeof OnNotFound === "function") {
                        OnNotFound();
                    }
                }
            } catch (err) {
               setTaskData(null);
               if(typeof OnNotFound === "function") {
                OnNotFound();
               }
            } finally {
                setIsLoadingTask(false);
            }
        };

        fetchTaskDetails();
    }, [taskId, projectId]);

    const [isTaskOwner, setIsTaskOwner] = useState(false);

    // Sync state with task prop changes (fallback)
    useEffect(() => {
        if (taskData && !isLoadingTask) {
            setIsTaskOwner(taskData.created_by === parseInt(currentUserId));
            setTaskTitle(taskData.title || taskData.name || '');

            const description = (taskData.description && taskData.description.replaceAll('<p></p>', '<p><br></p>')) || '';
            setTaskDescription(description);
            const assigneesData = taskData.assignees || taskData.project_users || [];
            setAssignees(mapAssignees(assigneesData));
            setStartDate(taskData.start_date || taskData.startDate || taskData.start_at || taskData.start_at || '');
            setDueDate(taskData.due_date || taskData.dueDate || taskData.due_at || '');
            setPriority(taskData.priority !== undefined ? taskData.priority : 0);
            setProjectName(taskData.project?.title || taskData.project?.name || taskData.project_name || '');
        }
    }, [taskData, isLoadingTask]);

    // Filter assignee search from project users only
    useEffect(() => {
        if (!assigneeSearchQuery.trim()) {
            setAssigneeSearchResults([]);
            return;
        }

        const debounceTimer = setTimeout(() => {
            const projectUsers = taskData?.project_users || [];
            const searchLower = assigneeSearchQuery.toLowerCase().trim();
            
            const filteredUsers = projectUsers.filter(user => {
                const userId = user.ID || user.id;
                const isAlreadyAssigned = assignees.some(a => (a.ID || a.id) === userId);
                if (isAlreadyAssigned) return false;
                
                const name = (user.display_name || user.name || '').toLowerCase();
                const email = (user.user_email || user.email || '').toLowerCase();
                return name.includes(searchLower) || email.includes(searchLower);
            });
            
            setAssigneeSearchResults(filteredUsers);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [assigneeSearchQuery, taskData?.project_users, assignees]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target) &&
                assigneeSearchRef.current && !assigneeSearchRef.current.contains(event.target)) {
                setShowAssigneeDropdown(false);
            }
            if (showDueDatePicker && 
                datePickerWrapperRef.current && 
                !datePickerWrapperRef.current.contains(event.target) &&
                !event.target.closest('.task-detail-due-date-wrapper')) {
                setShowDueDatePicker(false);
            }
            if (showStartDatePicker && 
                startDatePickerWrapperRef.current && 
                !startDatePickerWrapperRef.current.contains(event.target) &&
                !event.target.closest('.task-detail-start-date-wrapper')) {
                setShowStartDatePicker(false);
            }
            if (isEditingDescription && 
                descriptionEditorRef.current && 
                !descriptionEditorRef.current.contains(event.target) &&
                !event.target.closest('.task-detail-description-editor-wrapper')
            ) {
                setIsEditingDescription(false);
            }
        };

        if (showAssigneeDropdown || showDueDatePicker || showStartDatePicker || isEditingDescription) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAssigneeDropdown, showDueDatePicker, showStartDatePicker, isEditingDescription]);

    // Format date for input
    const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        try {
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (e) {
            return '';
        }
    };

    // Helper function to refresh complete task data
    const refreshTaskData = async () => {
        const currentTask = taskData;
        if (!currentTask?.id || !projectId) return;
        
        try {
            const response = await getTaskById(projectId, currentTask.id);
            if (response && response.data) {
                setTaskData(response.data);
                setRefreshTaskActivities(true);
                // Update local state with fetched data
            }
        } catch (err) {
            console.error('Failed to refresh task data:', err);
        }
    };

    const handleSaveTitle = async () => {
        const currentTask = taskData || task;
        if (taskTitle.trim() && taskTitle.trim() !== (currentTask?.title || currentTask?.name)) {
            await updateTask(projectId, currentTask.id, { title: taskTitle.trim(), action: 'update_title' });

            if(typeof onRefreshMainTaskData === "function") {
                onRefreshMainTaskData(currentTask?.task_list_id);
            }
            // Refresh complete task data after update
            await refreshTaskData();
        }
        setIsEditingTitle(false);
    };

    const handleSaveDescription = async (editorData) => {
        const currentTask = taskData || task;

        if (!currentTask || !currentTask.id || !projectId) {
            return;
        }

        const htmlContent = editorData?.htmlContent || editorData?.content || '';

        await updateTask(projectId, currentTask.id, { description: htmlContent, action: 'update_description' });

        if (typeof onRefreshMainTaskData === "function") {
            onRefreshMainTaskData(currentTask?.task_list_id);
        }
        // Keep local state in sync so view mode shows latest description
        setTaskDescription(htmlContent);
        // Refresh complete task data after update
        await refreshTaskData();
        setIsEditingDescription(false);
    };

    const handleSelectAssignee = async (user) => {
        setAssigneeSearchQuery('');
        setShowAssigneeDropdown(false);
        const currentTask = taskData || task;
        const assigneeId = user.ID || user.id;
        // if (addAssigneeToTask) {
        //     await addAssigneeToTask(currentTask.id, currentTask.task_list_id, assigneeId);
        // }
        await addAssigneeToTask(projectId, currentTask.id, assigneeId);
        // Refresh complete task data after update
        await refreshTaskData();
    };

    const handleRemoveAssignee = async (assigneeId, e) => {
        e.stopPropagation();
        const currentTask = taskData || task;
        await updateTask(projectId, currentTask.id, { assignee_id: assigneeId, action: 'remove_assignee' });

        if(typeof onRefreshMainTaskData === "function") {
            onRefreshMainTaskData(currentTask?.task_list_id);
        }
        // Refresh complete task data after update
        await refreshTaskData();
    };

    const handleDueDateChange = async (newDate) => {
        setDueDate(newDate);
        //setShowDueDatePicker(false);
        const currentTask = taskData || task;
        await updateTask(projectId, currentTask.id, { due_date: newDate || '', action: 'update_due_date' });

        if(typeof onRefreshMainTaskData === "function") {
            onRefreshMainTaskData(currentTask?.task_list_id);
        }
        // Refresh complete task data after update
        await refreshTaskData();
    };

    const handlePriorityChange = async (newPriority) => {
        const priorityValue = parseInt(newPriority, 10);
        setPriority(priorityValue);
        const currentTask = taskData || task;
        await updateTask(projectId, currentTask.id, { priority: priorityValue, action: 'update_priority' });

        if(typeof onRefreshMainTaskData === "function") {
            onRefreshMainTaskData(currentTask?.task_list_id);
        }
        // Refresh complete task data after update
        await refreshTaskData();
    };


    const handleToggleComplete = async () => {
        const currentTask = taskData || task;
        const newStatus = currentTask.status === 1 ? 0 : 1;
        await updateTask(projectId, currentTask.id, { status: newStatus, action: 'update_status' });
        if(typeof onRefreshMainTaskData === "function") {
            onRefreshMainTaskData(currentTask?.task_list_id);
        }
        // Refresh complete task data after update
        await refreshTaskData();
    };

    const handleStatusChange = async (newStatus) => {
        const currentTask = taskData || task;
        await updateTask(projectId, currentTask.id, { status: newStatus, action: 'update_status' });
        if(typeof onRefreshMainTaskData === "function") {
            onRefreshMainTaskData(currentTask?.task_list_id);
        }
        // Refresh complete task data after update
        await refreshTaskData();
    };

    const handleAddSubtask = async () => {
        const currentTask = taskData;
        if (!subtaskText.trim() || !projectId || !currentTask?.task_list_id) return;
        
        try {
            const response = await createTask(projectId, {
                title: subtaskText.trim(),
                task_list_id: currentTask?.task_list_id,
                parent_id: currentTask?.id
            });
            if (response && response.data) {
                setSubtaskText('');
                setIsAddingSubtask(false);

                if(typeof onRefreshMainTaskData === "function") {
                    onRefreshMainTaskData(currentTask?.task_list_id);
                }
                // Refresh complete task data to get updated subtasks
                await refreshTaskData();
            }
        } catch (err) {
            console.error('Failed to create subtask:', err);
            alert('Failed to create subtask. Please try again.');
        }
    };

    const handleUpdateSubtask = async (subtaskId, parentTaskId, taskListId, subtaskData) => {
        if (!projectId) return;
        try {
            await updateTask(projectId, subtaskId, subtaskData);
            if(typeof onRefreshMainTaskData === "function") {
                onRefreshMainTaskData(currentTask?.task_list_id);
            }
            await refreshTaskData();
        } catch (err) {
            console.error('Failed to update subtask:', err);
            alert('Failed to update subtask. Please try again.');
        }
    };

    const handleDeleteSubtask = async (subtaskId, parentTaskId, taskListId) => {
        if (!projectId || !taskListId) return;
        try {
            const response = await deleteTask(projectId, taskListId, subtaskId);
            if (response && response.data) {
                if(typeof onRefreshMainTaskData === "function") {
                    onRefreshMainTaskData(currentTask?.task_list_id);
                }
                await refreshTaskData();
            }
        } catch (err) {
            console.error('Failed to delete subtask:', err);
            alert('Failed to delete subtask. Please try again.');
        }
    };

    const handleAddAssigneeToSubtask = async (taskId, taskListId, assigneeId) => {
        if (!projectId) return;
        try {
            await addAssigneeToTask(projectId, taskId, assigneeId);
            await refreshTaskData();
        } catch (err) {
            console.error('Failed to add assignee to subtask:', err);
            alert('Failed to add assignee to subtask. Please try again.');
        }
    };

    const handleAttachmentClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const currentTask = taskData;
        if (!currentTask?.id || !projectId) {
            alert('Task information is missing. Please try again.');
            return;
        }

        setIsUploadingAttachment(true);
        try {
            const formData = new FormData();

            // Append all selected files as attachments
            Array.from(files).forEach((file) => {
                formData.append('attachments[]', file, file.name);
            });

            const response = await uploadTaskAttachment(projectId, currentTask.id, formData);
            if (response && response.data) {
                // Refresh task data to show new attachments
                await refreshTaskData();
                // Also refresh main task data
                if (typeof onRefreshMainTaskData === "function") {
                    onRefreshMainTaskData(currentTask?.task_list_id);
                }
            }
        } catch (err) {
            console.error('Failed to upload attachment:', err);
            alert('Failed to upload attachment. Please try again.');
        } finally {
            setIsUploadingAttachment(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDeleteAttachment = async (fileId, e) => {
        e.stopPropagation();
        const currentTask = taskData;
        if (!currentTask?.id || !projectId || !fileId) return;

        if (!window.confirm('Are you sure you want to delete this attachment?')) {
            return;
        }

        try {
            await deleteTaskAttachment(projectId, currentTask.id, fileId);
            // Refresh task data to remove deleted attachment
            await refreshTaskData();
            // Also refresh main task data
            if (typeof onRefreshMainTaskData === "function") {
                onRefreshMainTaskData(currentTask?.task_list_id);
            }
        } catch (err) {
            console.error('Failed to delete attachment:', err);
            alert('Failed to delete attachment. Please try again.');
        }
    };

    const handleCopyLink = async () => {
        try {
            // Copy current page URL to clipboard
            const currentUrl = window.location.href;
            await navigator.clipboard.writeText(currentUrl);
            
            // Show feedback
            setLinkCopied(true);
            setTimeout(() => {
                setLinkCopied(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea');
                textArea.value = window.location.href;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                
                setLinkCopied(true);
                setTimeout(() => {
                    setLinkCopied(false);
                }, 2000);
            } catch (fallbackErr) {
                alert('Failed to copy link. Please try again.');
            }
        }
    };

    // Use taskData if available
    const currentTask = taskData;

    if(isLoadingTask) {
        return (
            <div className="task-detail-side-panel-overlay" onClick={onClose}>
                <div className="task-detail-side-panel" onClick={(e) => e.stopPropagation()}>
                    <div className="task-detail-header">
                        <div className="task-detail-header-left">
                            <i className="fa-solid fa-spinner fa-spin"></i>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="task-detail-side-panel-overlay" onClick={onClose}>
            <div className="task-detail-side-panel" onClick={(e) => e.stopPropagation()}>
                {/* Header with Actions */}
                <div className="task-detail-header">
                    <div className="task-detail-header-left">
                        {isLoadingTask ? (
                            <div>Loading task details...</div>
                        ) : (
                            <>
                                {!!currentTask?.parent_id && typeof onOpenSubtaskDetail === "function" && projectId && (
                                    <button
                                        type="button"
                                        className="asana-task-back-btn"
                                        onClick={() => onOpenSubtaskDetail(projectId, currentTask.parent_id)}
                                    >
                                        <i className="fa-solid fa-chevron-left"></i>
                                    </button>
                                )}
                                {projectName && (
                                    <div className="task-detail-project-name">
                                        <i className="fa-solid fa-folder"></i>
                                        <span>{projectName}</span>
                                    </div>
                                )}
                                {/* Status Dropdown */}
                                <StatusDropdown
                                    currentStatus={currentTask?.status ?? 0}
                                    onStatusChange={handleStatusChange}
                                    className="task-detail-status-wrapper"
                                    buttonClassName="task-detail-status-btn"
                                    size="medium"
                                    showLabel={true}
                                />
                                <div className="task-detail-actions">
                                    <span 
                                        className="task-detail-action-icon" 
                                        title="Attachment"
                                        onClick={handleAttachmentClick}
                                        disabled={isUploadingAttachment}
                                    >
                                        {isUploadingAttachment ? (
                                            <i className="fa-solid fa-spinner fa-spin"></i>
                                        ) : (
                                            // Use inline SVG for paperclip
                                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <svg viewBox="-8 0 32 32" width="24" 
                                                    height="24" version="1.1" xmlns="http://www.w3.org/2000/svg"  fill="#5f6368bd" transform="rotate(180)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>attachment</title> <desc>Created with Sketch Beta.</desc> <defs> </defs> <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" ><g id="Icon-Set-Filled" transform="translate(-214.000000, -153.000000)" fill="#5f6368bd"><path d="M228,157 L228,177 C228,180.313 225.313,183 222,183 C218.687,183 216,180.313 216,177 L216,159 C216,156.791 217.791,155 220,155 C222.209,155 224,156.791 224,159 L224,177 C224,178.104 223.104,179 222,179 C220.896,179 220,178.104 220,177 L220,161 L218,161 L218,177 C218,179.209 219.791,181 222,181 C224.209,181 226,179.209 226,177 L226,159 C226,155.687 223.313,153 220,153 C216.687,153 214,155.687 214,159 L214,178 C214.493,181.945 217.921,185 222,185 C226.079,185 229.507,181.945 230,178 L230,157 L228,157" id="attachment"> </path> </g> </g> </g></svg>
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="task-detail-actions" style={{ position: 'relative' }}>
                                    <span 
                                        className="task-detail-action-icon" 
                                        title={linkCopied ? "Link copied!" : "Copy link"}
                                        onClick={handleCopyLink}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" 
                                                height="24" transform="rotate(90)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M5.63605 5.63605C7.19815 4.07395 9.73081 4.07395 11.2929 5.63605L14.1213 8.46448C15.6834 10.0266 15.6834 12.5592 14.1213 14.1213C13.7308 14.5119 13.0976 14.5119 12.7071 14.1213C12.3166 13.7308 12.3166 13.0976 12.7071 12.7071C13.4882 11.9261 13.4882 10.6597 12.7071 9.87869L9.87869 7.05026C9.09764 6.26922 7.83131 6.26922 7.05026 7.05026C6.26922 7.83131 6.26922 9.09764 7.05026 9.87869L7.75737 10.5858C8.1479 10.9763 8.14789 11.6095 7.75737 12C7.36685 12.3905 6.73368 12.3905 6.34316 12L5.63605 11.2929C4.07395 9.73081 4.07395 7.19815 5.63605 5.63605ZM11.2929 9.8787C11.6834 10.2692 11.6834 10.9024 11.2929 11.2929C10.5119 12.074 10.5119 13.3403 11.2929 14.1213L14.1213 16.9498C14.9024 17.7308 16.1687 17.7308 16.9498 16.9498C17.7308 16.1687 17.7308 14.9024 16.9498 14.1213L16.2427 13.4142C15.8521 13.0237 15.8521 12.3905 16.2427 12C16.6332 11.6095 17.2663 11.6095 17.6569 12L18.364 12.7071C19.9261 14.2692 19.9261 16.8019 18.364 18.364C16.8019 19.9261 14.2692 19.9261 12.7071 18.364L9.8787 15.5356C8.3166 13.9735 8.3166 11.4408 9.8787 9.8787C10.2692 9.48817 10.9024 9.48817 11.2929 9.8787Z" fill="#5f6368bd"></path> </g></svg>
                                        </span>
                                    </span>
                                    {linkCopied && (
                                        <span 
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                marginTop: '8px',
                                                padding: '6px 12px',
                                                backgroundColor: '#333',
                                                color: '#fff',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                whiteSpace: 'nowrap',
                                                zIndex: 1000,
                                                pointerEvents: 'none',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                            }}
                                        >
                                            Link copied!
                                        </span>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                            </>
                        )}
                    </div>
                    <button className="task-detail-close-btn" onClick={onClose}>
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                {/* Privacy Banner */}
                {/* <div className="task-detail-privacy-banner">
                    <div className="task-detail-privacy-content">
                        <i className="fa-solid fa-lock"></i>
                        <span>This task is private to members of this project.</span>
                    </div>
                    <button className="task-detail-make-public-btn">Make public</button>
                </div> */}

                {/* Content */}
                <div className="task-detail-content">
                   

                    {/* Task Title */}
                    <div className="task-detail-title-section">
                        {isEditingTitle ? (
                            <input
                                ref={titleInputRef}
                                type="text"
                                className="task-detail-title-input"
                                value={taskTitle}
                                onChange={(e) => setTaskTitle(e.target.value)}
                                onBlur={handleSaveTitle}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSaveTitle();
                                    } else if (e.key === 'Escape') {
                                        setTaskTitle(task?.title || task?.name || '');
                                        setIsEditingTitle(false);
                                    }
                                }}
                                autoFocus
                            />
                        ) : (
                            <h1 
                                className={"task-detail-title " + (taskData?.status === 1 ? 'asana-completed-task' : '')}
                                onClick={() => setIsEditingTitle(true)}
                            >
                                {isLoadingTask ? 'Loading...' : (taskTitle || 'Untitled task')}
                            </h1>
                        )}
                    </div>

                    {/* Assignee and Due Date Row */}
                    <div className="task-detail-info-row">
                        {/* Assignee */}
                        <div className="task-detail-info-item" style={{ display: taskData?.parent_id ? 'none' : 'block' }}>
                            <label className="task-detail-info-label">Assignee</label>
                            <div className="task-detail-assignee-wrapper" ref={assigneeSearchRef}>
                                <div className="task-detail-assignees-row">
                                    {assignees.length > 0 ? (
                                        assignees.map((assignee) => (
                                            <div 
                                                key={assignee.ID || assignee.id}
                                                className="task-detail-assignee-avatar-item"
                                                title={assignee.display_name || assignee.name}
                                            >
                                                {assignee.image || assignee.avatar_url ? (
                                                    <img 
                                                        src={assignee.image || assignee.avatar_url} 
                                                        alt={assignee.display_name || assignee.name}
                                                    />
                                                ) : (
                                                    <div className="task-detail-avatar-placeholder">
                                                        {(assignee.display_name || assignee.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                {(isTaskOwner || assignee.created_by === parseInt(currentUserId)) && (
                                                    <button
                                                        type="button"
                                                        className="task-detail-remove-assignee-btn"
                                                        onClick={(e) => handleRemoveAssignee(assignee.ID || assignee.id, e)}
                                                        title="Remove assignee"
                                                    >
                                                        <i className="fa-solid fa-times"></i>
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    ) : null}
                                    <span 
                                        className="task-detail-add-assignee-btn"
                                        onClick={() => setShowAssigneeDropdown(true)}
                                        title="Add assignee"
                                    >
                                        <i className="fa-solid fa-plus"></i>
                                    </span>
                                </div>
                                {showAssigneeDropdown && (
                                    <div className="task-detail-assignee-dropdown" ref={assigneeDropdownRef}>
                                        <input
                                            type="text"
                                            className="task-detail-assignee-search"
                                            placeholder="Search assignee..."
                                            value={assigneeSearchQuery}
                                            onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                                            autoFocus
                                        />
                                        {assigneeSearchResults.length > 0 ? (
                                            <div className="task-detail-assignee-results">
                                                {assigneeSearchResults.map((user) => (
                                                    <div
                                                        key={user.ID || user.id}
                                                        className="task-detail-assignee-result-item"
                                                        onClick={() => handleSelectAssignee(user)}
                                                    >
                                                        <div className="task-detail-assignee-avatar">
                                                            {user.image || user.avatar_url ? (
                                                                <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                                            ) : (
                                                                <div className="task-detail-avatar-placeholder">
                                                                    {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span>{user.display_name || user.name || 'User'}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : assigneeSearchQuery.trim() ? (
                                            <div className="task-detail-search-empty">No users found</div>
                                        ) : (
                                            <div className="task-detail-assignee-results">
                                                {(taskData?.project_users || []).filter(user => {
                                                    const userId = user.ID || user.id;
                                                    return !assignees.some(a => (a.ID || a.id) === userId);
                                                }).map((user) => (
                                                    <div
                                                        key={user.ID || user.id}
                                                        className="task-detail-assignee-result-item"
                                                        onClick={() => handleSelectAssignee(user)}
                                                    >
                                                        <div className="task-detail-assignee-avatar">
                                                            {user.image || user.avatar_url ? (
                                                                <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                                            ) : (
                                                                <div className="task-detail-avatar-placeholder">
                                                                    {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span>{user.display_name || user.name || 'User'}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Due Date */}
                        <div className="task-detail-info-item" style={{ display: taskData?.parent_id ? 'none' : 'block' }}>
                            <label className="task-detail-info-label">Due date</label>
                            <div className="task-detail-due-date-wrapper" ref={datePickerWrapperRef}>
                                {dueDate ? (
                                    <div 
                                        className="task-detail-due-date-value"
                                        onClick={() => setShowDueDatePicker(true)}
                                        
                                    >
                                        <div className="task-detail-icon-circle">
                                            <i className="fa-solid fa-calendar"></i>
                                        </div>
                                        <span>{new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                    </div>
                                ) : (
                                    <div 
                                        className="task-detail-due-date-value"
                                        onClick={() => setShowDueDatePicker(true)}
                                    >
                                        <div className="task-detail-icon-circle">
                                            <i className="fa-solid fa-calendar"></i>
                                        </div>
                                        <span>No due date</span>
                                    </div>
                                )}
                                {showDueDatePicker && (
                                    <div className="task-detail-due-date-picker-wrapper">
                                        <input
                                            type="date"
                                            className="task-detail-due-date-picker"
                                            value={formatDateForInput(dueDate)}
                                            onChange={(e) => handleDueDateChange(e.target.value)}
                                            autoFocus
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Priority */}
                        <div className="task-detail-info-item">
                            <label className="task-detail-info-label">Priority</label>
                            <div className="task-detail-priority-wrapper">
                                <select
                                    className="task-detail-priority-select"
                                    value={priority}
                                    onChange={(e) => handlePriorityChange(e.target.value)}
                                >
                                    <option value="0">Low</option>
                                    <option value="1">Medium</option>
                                    <option value="2">High</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Recurring Section */}
                    <TaskRecurringSection
                        taskData={taskData}
                        projectId={projectId}
                        onRefreshMainTaskData={onRefreshMainTaskData}
                        onRefreshTaskData={refreshTaskData}
                        style={{ display: taskData?.parent_id ? 'none' : 'block' }}
                    />

                    {/* Description Section */}
                    <div className="task-detail-field">
                        <label className="task-detail-field-label">Description</label>
                        {isEditingDescription ? (
                            <div className="task-detail-description-editor-wrapper" ref={descriptionEditorRef}>
                                <ProseDescriptionEditor
                                    key={`description-editor-${currentTask?.id || taskId}`}
                                    onSubmit={handleSaveDescription}
                                    placeholder="What is this task about?"
                                    initialValue={taskDescription || ''}
                                />
                            </div>
                        ) : (
                            <div
                                className="task-detail-description"
                                onClick={() => setIsEditingDescription(true)}
                            >
                                {taskDescription ? (
                                    <div
                                        className="task-detail-description-content"
                                        dangerouslySetInnerHTML={{ __html: taskDescription }}
                                    />
                                ) : (
                                    <span className="task-detail-description-placeholder">
                                        What is this task about?
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Subtasks Section */}
                    <div className="task-detail-subtasks-section">
                        <label className="task-detail-field-label">Subtasks</label>
                            <div className="task-detail-subtasks-table-wrapper">
                                <table className="task-detail-subtasks-table">
                                    <thead>
                                        {/* <tr>
                                            <th className="task-detail-subtask-col-name">Name</th>
                                            <th className="task-detail-subtask-col-actions">Actions</th>
                                        </tr> */}
                                    </thead>
                                    <tbody>
                                        {currentTask?.subtasks && currentTask.subtasks.length > 0 ? (
                                            currentTask.subtasks.map((subtask) => (
                                                <SubtaskRow
                                                    key={subtask.id}
                                                    subtask={subtask}
                                                    currentUserId={currentUserId}
                                                    parentTaskData={currentTask}
                                                    taskListId={currentTask?.task_list_id}
                                                    parentTaskId={currentTask?.id}
                                                    projectUsers={taskData?.project_users || []}
                                                    onUpdate={handleUpdateSubtask}
                                                    onDelete={handleDeleteSubtask}
                                                    onOpenSubtaskDetail={onOpenSubtaskDetail}
                                                    projectId={projectId}
                                                />
                                            ))
                                        ) : null}
                                        {/* Add Subtask Input Row */}
                                        {isAddingSubtask && (
                                            <tr className="task-detail-subtask-input-row asana-default-task-input-row">
                                                <td colSpan="2">
                                                    <div className="task-detail-subtask-input-wrapper asana-task-name-cell">
                                                        <input
                                                            ref={subtaskInputRef}
                                                            type="text"
                                                            className="task-detail-subtask-input"
                                                            placeholder="Add a subtask"
                                                            value={subtaskText}
                                                            onChange={(e) => setSubtaskText(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && subtaskText.trim()) {
                                                                    e.preventDefault();
                                                                    handleAddSubtask();
                                                                } else if (e.key === 'Escape') {
                                                                    setSubtaskText('');
                                                                    setIsAddingSubtask(false);
                                                                }
                                                            }}
                                                            onBlur={(e) => {
                                                                // Don't cancel if clicking on the save button
                                                                if (e.relatedTarget && e.relatedTarget.closest('.task-detail-save-subtask-btn')) {
                                                                    return;
                                                                }
                                                                setTimeout(() => {
                                                                    if (!subtaskInputRef.current || document.activeElement !== subtaskInputRef.current) {
                                                                        setSubtaskText('');
                                                                        setIsAddingSubtask(false);
                                                                    }
                                                                }, 200);
                                                            }}
                                                            autoFocus
                                                        />
                                                        {subtaskText.trim() && (
                                                            <button
                                                                type="button"
                                                                className="asana-save-task-arrow-btn"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleAddSubtask();
                                                                }}
                                                                onMouseDown={(e) => {
                                                                    e.preventDefault(); // Prevent input blur
                                                                }}
                                                                title="Save subtask"
                                                            >
                                                                <i className="fa-solid fa-check"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        
                        {/* Add Subtask Button */}
                        {!isAddingSubtask && (
                            <div className="task-detail-add-subtask">
                                <button 
                                    className="task-detail-add-subtask-btn"
                                    onClick={() => setIsAddingSubtask(true)}
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    Add subtask
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Task Attachments Section */}
                    {currentTask?.files && currentTask.files.length > 0 && (
                        <div className="task-detail-field">
                            <label className="task-detail-field-label">Attachments</label>
                            <div className="task-detail-attachments-block">
                                <div className="task-detail-attachments">
                                    {currentTask.files.map((file, index) => {
                                        const previewUrl = file.file_url;
                                        const displayName = file.file_name || 'Attachment';
                                        const downloadUrl = file.file_url;
                                        const key = file.id || file.attachment_id || index;
                                        const mime = file.mime_type || '';
                                        const type = (file.type || '').toLowerCase();
                                        const isPhoto =
                                            type === 'photo' ||
                                            mime.startsWith('image/');
                                        const isVideo =
                                            type === 'video' ||
                                            mime.startsWith('video/');

                                        if (isPhoto) {
                                            return (
                                                <div
                                                    key={key}
                                                    className="task-attachment-card task-attachment-card--image"
                                                >
                                                    {previewUrl ? (
                                                        <div className="task-attachment-thumb task-attachment-thumb--image">
                                                            <img
                                                                src={previewUrl}
                                                                alt={displayName}
                                                                className="task-attachment-thumb-img"
                                                            />
                                                            <div className="task-attachment-overlay">
                                                                <button
                                                                    className="task-attachment-preview-btn"
                                                                    onClick={() => setPreviewAttachment({
                                                                        url: previewUrl,
                                                                        name: displayName
                                                                    })}
                                                                    title="Preview"
                                                                >
                                                                    <i className="fa-solid fa-eye"></i>
                                                                </button>
                                                                {downloadUrl && (
                                                                    <a
                                                                        href={downloadUrl}
                                                                        download
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="task-attachment-download-btn"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        title="Download"
                                                                    >
                                                                        <i className="fa-solid fa-download"></i>
                                                                    </a>
                                                                )}
                                                                <button
                                                                    className="task-attachment-delete-btn"
                                                                    onClick={(e) => handleDeleteAttachment(file.id || file.attachment_id, e)}
                                                                    title="Delete attachment"
                                                                >
                                                                    <i className="fa-solid fa-times"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                        {/* // <span className="task-attachment-fallback">
                                                        //     {displayName}
                                                        // </span> */}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        }

                                        if (isVideo) {
                                            return (
                                                <div
                                                    key={key}
                                                    className="task-attachment-card task-attachment-card--video"
                                                >
                                                    {previewUrl ? (
                                                        <div className="task-attachment-video-wrapper">
                                                            <video
                                                                controls={false}
                                                                src={previewUrl}
                                                                className="task-attachment-video"
                                                            >
                                                                {displayName}
                                                            </video>
                                                            <div className="task-attachment-overlay">
                                                                {downloadUrl && (
                                                                    <a
                                                                        href={downloadUrl}
                                                                        download
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="task-attachment-download-btn"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        title="Download"
                                                                    >
                                                                        <i className="fa-solid fa-download"></i>
                                                                    </a>
                                                                )}
                                                                <button
                                                                    className="task-attachment-delete-btn"
                                                                    onClick={(e) => handleDeleteAttachment(file.id || file.attachment_id, e)}
                                                                    title="Delete attachment"
                                                                >
                                                                    <i className="fa-solid fa-times"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                        {/* // <span className="task-attachment-fallback">
                                                        //     {displayName}
                                                        // </span> */}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        }

                                        return (
                                            <div
                                                key={key}
                                                className="task-attachment-card task-attachment-card--file"
                                            >
                                                <div className="task-attachment-file-icon">
                                                    📄
                                                </div>
                                                <div className="task-attachment-file-meta">
                                                    <div className="task-attachment-filename">
                                                        {displayName}
                                                    </div>
                                                </div>
                                                <div className="task-attachment-file-overlay">
                                                    {downloadUrl ? (
                                                        <a
                                                            href={downloadUrl}
                                                            download
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="task-attachment-download-btn"
                                                            onClick={(e) => e.stopPropagation()}
                                                            title="Download"
                                                        >
                                                            <i className="fa-solid fa-download"></i>
                                                        </a>
                                                    ) : null}
                                                    <button
                                                        className="task-attachment-delete-btn"
                                                        onClick={(e) => handleDeleteAttachment(file.id || file.attachment_id, e)}
                                                        title="Delete attachment"
                                                    >
                                                        <i className="fa-solid fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {/* Add attachment button */}
                                    <div 
                                        className="task-attachment-card task-attachment-card--add"
                                        onClick={handleAttachmentClick}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="task-attachment-add-icon">
                                            <i className="fa-solid fa-plus"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Comments & Activity Section */}
                    <TaskCommentsActivitySection
                        projectId={projectId}
                        taskData={taskData}
                        currentUserId={currentUserId}
                        onRefreshTaskData={refreshTaskData}
                        refreshTaskActivitiesData={refreshTaskActivities}
                        setRefreshTaskActivitiesData={setRefreshTaskActivities}
                    />

                    {/*  Leave Task */}
                    <div className="task-detail-footer-section" style={{ display: 'none' }}>
                        
                        <button className="task-detail-leave-btn">
                            <i className="fa-solid fa-bell"></i>
                            Leave task
                        </button>
                    </div>
                </div>
            </div>

            {/* Attachment Preview Modal */}
            {previewAttachment && (
                <div 
                    className="octopus-preview-overlay"
                    onClick={() => setPreviewAttachment(null)}
                >
                    <div 
                        className="octopus-preview-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            className="octopus-preview-close"
                            onClick={() => setPreviewAttachment(null)}
                        >
                            <span aria-hidden="true">×</span>
                            <span className="octopus-sr-only">Close preview</span>
                        </button>
                        <img
                            src={previewAttachment.url}
                            alt={previewAttachment.name}
                            className="octopus-preview-image"
                        />
                        {previewAttachment.name && (
                            <div className="octopus-preview-title">
                                {previewAttachment.name}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskDetailSidePanel;
